import { IColumn, Icon, IGroup, IIconProps } from '@fluentui/react';
import { CSSProperties, forwardRef, HTMLAttributes, ReactElement, Ref } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { IBasicColumn } from '../BasicTable/BasicTable';
import { DetailsListSizes } from '../DetailsList/DetailsList.styles';
import { IListCellClassNames, IListCellStyles, listCellStylesDefault } from './ListCell.styles';

export interface IListCellProps<T extends { [key: string]: any }>
  extends IComponent<IListCellStyles>,
    Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  headerText?: string;
  text?: string;
  data?: T;
  width?: number | string;
  group?: IGroup;
  hasLeftOpenClose?: boolean;
  selected?: boolean;
  first?: boolean;
  last?: boolean;
  column?: IColumn | IBasicColumn;
  columnIndex?: number;
  rowIndex?: number;
  iconProps?: IIconProps;
  onRender?: (data?: T, index?: number, column?: IColumn | IBasicColumn, rowIndex?: number) => ReactElement;
  onRenderPrefix?: (data?: any, index?: number, column?: IColumn | IBasicColumn, rowIndex?: number) => ReactElement;
  onRenderHeader?: (data?: any, index?: number, column?: IColumn | IBasicColumn, rowIndex?: number) => ReactElement;
  onRenderText?: (data?: any, index?: number, column?: IColumn | IBasicColumn, rowIndex?: number) => ReactElement;
  emptyMessage?: string;
  errorMessage?: string;
  changed?: boolean;
  disabled?: boolean;
  canDisableCustomContents?: boolean;
  hasDepth?: boolean;
  iconFieldName?: string;
  editable?: boolean;
}

const getGroupPadding = (group?: IGroup, hasLeftOpenClose?: boolean, first?: boolean, last?: boolean) => {
  let style: CSSProperties = {};
  if ((first || last) && group) {
    style = hasLeftOpenClose
      ? first
        ? { paddingLeft: DetailsListSizes.GroupButtonLeftPadding }
        : {}
      : last
      ? { paddingRight: DetailsListSizes.GroupButtonRightPadding }
      : {};
  }
  return style;
};

const renderDepthLine = (classNames: IListCellClassNames, hasNextSibling: boolean, last: boolean, i: number) => {
  let className: string; // hasNextSibling && !last
  if (last) {
    if (hasNextSibling) {
      className = classNames.depthHasNextSibling; // ㅏ
    } else {
      className = classNames.depthEnd; // L
    }
  } else {
    if (hasNextSibling) {
      className = classNames.depthLine; // |
    } else {
      className = classNames.depthEmpty;
    }
  }
  return (
    <div key={i} className={className}>
      <div></div>
      <div></div>
    </div>
  );
};

const renderDepthLines = (classNames: IListCellClassNames, depthWithHasNextSibling: boolean[] = []) => {
  const last = depthWithHasNextSibling.length - 1;
  return depthWithHasNextSibling.length ? (
    <div className={classNames.depth} data-depth={depthWithHasNextSibling.length}>
      {depthWithHasNextSibling.map((hasNextSibling, i) => renderDepthLine(classNames, hasNextSibling, i === last, i))}
    </div>
  ) : null;
};

const renderIcon = (classNames: IListCellClassNames, iconProps: IIconProps) => {
  return (
    <div className={classNames.icon}>
      <div>
        <Icon {...iconProps} />
      </div>
    </div>
  );
};

export const ListCell = forwardRef(
  <T extends { [key: string]: any }>(props: IListCellProps<T>, ref: Ref<HTMLDivElement>) => {
    const {
      styles,
      className,
      data,
      headerText,
      text,
      width,
      selected,
      group,
      hasLeftOpenClose,
      first,
      last,
      column,
      columnIndex,
      rowIndex,
      disabled,
      emptyMessage,
      errorMessage,
      changed,
      canDisableCustomContents,
      iconProps,
      hasDepth,
      iconFieldName,
      editable,
      onRender,
      onRenderPrefix,
      onRenderHeader,
      onRenderText,
      ...elementProps
    } = props;
    const customContent = onRender ? onRender(data, columnIndex, column, rowIndex) : null;
    const prefixContent = onRenderPrefix ? onRenderPrefix(data, columnIndex, column, rowIndex) : null;
    const headerContent = onRenderHeader ? onRenderHeader(data, columnIndex, column, rowIndex) : null;
    const textContent = onRenderText ? onRenderText(data, columnIndex, column, rowIndex) : null;
    const classNames = useClassNames<Partial<IListCellStyles>, IListCellClassNames>(
      'ListCell',
      listCellStylesDefault,
      styles,
      className,
      selected && 'is-selected',
      first && 'is-first',
      last && 'is-last',
      disabled && (canDisableCustomContents || !customContent) && 'is-disabled',
      !disabled && editable && 'is-editable',
      changed && 'is-changed',
      !!errorMessage && 'has-error',
      hasDepth && 'has-depth',
      iconProps && 'has-icon'
    );
    return (
      <div
        {...elementProps}
        ref={ref}
        className={classNames.root}
        style={{ width, ...getGroupPadding(group, hasLeftOpenClose, first, last) }}
        data-test={props['data-test'] || 'list-cell'}
        aria-colindex={(columnIndex || 0) + 1}
        data-row-index={rowIndex}
        data-col-index={columnIndex}
        title={`${text}${errorMessage ? ` * Error: ${errorMessage}` : ''}${
          !errorMessage && changed ? ' * Cell value is changed' : ''
        }`}
      >
        {hasDepth && renderDepthLines(classNames, data?.depthWithHasNextSibling)}
        {iconProps &&
          renderIcon(classNames, {
            ...iconProps,
            iconName: iconFieldName && data ? data[iconFieldName] : iconProps.iconName,
          })}
        {prefixContent}
        {customContent ? (
          customContent
        ) : (
          <div className={classNames.content}>
            {headerText && (
              <div className={classNames.header} title={headerText}>
                {headerContent || headerText}
              </div>
            )}
            {(text || textContent || emptyMessage) && (
              <div className={classNames.text}>
                {!text && !textContent && emptyMessage ? emptyMessage : textContent || text}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
) as <T extends { [key: string]: any }>(props: IListCellProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
